import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  User,
  Organization,
  CreateOrganizationPayload,
  SuggestedUsersAndOrganizations,
  RemoveMemberParams,
  Spark,
  Post,
  Cities,
  InteractionType,
  Following,
  Follower,
  Metric,
  FollowUserResponse,
  EditGoalResponsePayload,
  NotificationResponse,
  State,
  Location,
  BlackFundMetrics,
  BlackFundMetricsFormData,
  Report,
  CreateReportPayload,
} from "./types";
import getBaseQueryWithReauth from "../helpers/getBaseQueryWithReauth";
import { sanitizeOrganizationPayload } from "../../src/utils/apiPayloads";

const baseQuery = fetchBaseQuery({
  // @ts-ignore
  baseUrl: `${import.meta.env.VITE_COMMUNITY_ENDPOINT}`,
  prepareHeaders: async (headers, { getState }) => {
      const rootState = getState() as { auth: { token: string; } }
      const token = rootState.auth?.token
      if (rootState) {
        headers.set("Authorization", `Bearer ${token}`);
      }
    return headers;
  },
})

export const api = createApi({
  reducerPath: "api",
  baseQuery: getBaseQueryWithReauth(baseQuery),
  tagTypes: [
    "User",
    "Organization",
    "Spark",
    "Post",
    "City",
    "State",
    "MetricDescription",
    "Notification",
    "Report",
    "Funders",
    "Programs",
    "FunderMetrics",
  ],
  endpoints: (builder) => ({
    getProfile: builder.query<User, void>({
      query: () => "/user",
      // @ts-ignore
      providesTags: (result, error, arg) =>
        result
          ? [
              { type: "User" as const, id: result.id },
              ...(result.organizations?.map(({ id }) => ({
                type: "Organization" as const,
                id,
              })) || ["Organization"]),
            ]
          : ["User", "Organization"],
    }),
    deactivateUser: builder.mutation<User, void>({
      query: () => ({
        url: "/user/deactivate",
        method: "PUT",
      }),
      invalidatesTags: ["User"],
    }),
    followUser: builder.mutation<
      FollowUserResponse,
      {
        userId: string;
        type: "USER" | "ORG";
      }
    >({
      query: (params) => ({
        url: `/user/following/${params.userId}?type=${params.type}`,
        method: "PUT",
      }),
      invalidatesTags: (result, error, arg) =>
        result?.id
          ? [
              {
                type:
                  result.followingType === "USER"
                    ? ("User" as const)
                    : ("Organization" as const),
                id: result.following,
              },
              "User",
            ]
          : ["User"],
    }),
    getUsersFollowed: builder.query<Following[], void>({
      query: () => ({
        url: "/user/following",
      }),
      providesTags: (result, error, arg) =>
        result
          ? [
              ...result.map(({ following }) => ({
                type: "User" as const,
                id: following,
              })),
              "User",
            ]
          : ["User"],
    }),
    deleteNotifications: builder.mutation<NotificationResponse, { id: string }>(
      {
        query: (params) => ({
          url: `/notifications/${params.id}`,
          method: "DELETE",
        }),
        invalidatesTags: ["Notification"],
      }
    ),
    tagUser: builder.mutation<
      NotificationResponse,
      {
        targetUserIds: string[];
        postId?: string | undefined;
        interactionId?: string | undefined;
      }
    >({
      query: ({ targetUserIds, postId, interactionId }) => ({
        url: `/users/tag`,
        method: "POST",
        body: {
          targetUserIds,
          postId,
          interactionId,
        },
      }),
      invalidatesTags: ["Notification"],
    }),
    getNotifications: builder.query<NotificationResponse[], void>({
      query: () => ({
        url: "/notifications",
        method: "GET",
      }),
      providesTags: ["Notification"]
      // async onCacheEntryAdded(
      //   arg,
      //   { updateCachedData, cacheDataLoaded, cacheEntryRemoved, getState }
      // ) {
      //   // create a websocket connection when the cache subscription starts
      //   const client = new Nes.Client(
      //     //@ts-ignore
      //     `${import.meta.env.VITE_WS_COMMUNITY_ENDPOINT}`
      //   );
      //   try {
      //     // wait for the initial query to resolve before proceeding
      //     await cacheDataLoaded;
      //     const cache = getState();

      //     // @ts-ignore
      //     const userId = cache.api.queries["getProfile(undefined)"]?.data.id; // Replace with the actual user ID

      //     const connectToWebSocket = async () => {
      //       try {
      //         const session = await Auth.currentSession();
      //         const token = session.getIdToken().getJwtToken();

      //         const nesTokenCallResponse = await axios({
      //           url: `${
      //             // @ts-ignore
      //             import.meta.env.VITE_COMMUNITY_ENDPOINT
      //           }/custom-nes/auth`,
      //           headers: {
      //             Authorization: `Bearer ${token}`,
      //           },
      //           method: "GET",
      //         });

      //         const NES_TOKEN = nesTokenCallResponse.data.token;

      //         await client.connect({
      //           auth: NES_TOKEN,
      //         });
      //         client.onConnect = () => {
      //           customConsole("log", "WebSocket connected ✅");
      //         };

      //         client.subscribe(
      //           `/notifications/${userId}`,
      //           (notification: NotificationResponse) => {
      //             toast("You just received a notification");
      //             updateCachedData((draft) => {
      //               // @ts-ignore
      //               draft.unshift(camelcaseKeys(notification));
      //             });
      //           }
      //         );

      //         client.onDisconnect = () => {
      //           customConsole("log", "WebSocket disconnected 🙄");
      //         };
      //       } catch (error) {
      //         customConsole("error", "WebSocket connection error 🟥:", error);
      //       }
      //     };

      //     connectToWebSocket();

      //     // Cleanup function
      //   } catch {
      //     // no-op in case `cacheEntryRemoved` resolves before `cacheDataLoaded`,
      //     // in which case `cacheDataLoaded` will throw
      //   }
      //   // cacheEntryRemoved will resolve when the cache subscription is no longer active
      //   await cacheEntryRemoved;
      //   // perform cleanup steps once the `cacheEntryRemoved` promise resolves
      //   client.disconnect();
      // },
    }),
    viewNotifications: builder.mutation<NotificationResponse, { id: string }>({
      query: (params) => ({
        url: `/notifications/${params.id}/view`,
        method: "PUT",
      }),
      invalidatesTags: ["Notification"],
    }),
    getFollowers: builder.query<Follower[], void>({
      query: () => ({
        url: "/user/followers",
      }),
      providesTags: (result, error, arg) =>
        result
          ? [
              ...result.map(({ id }) => ({
                type: "User" as const,
                id,
              })),
            ]
          : ["User"],
    }),
    unFollowAccount: builder.mutation<
      void,
      { type: "USER" | "ORG"; userId: string }
    >({
      query: (params) => ({
        url: `/user/following/${params.userId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : arg.type === "USER"
          ? [{ type: "User" as const, id: arg.userId }]
          : [{ type: "Organization" as const, id: arg.userId }],
    }),
    getUsers: builder.query<User[], void>({
      query: () => `/users`,
      providesTags: ["User"],
    }),
    getUserById: builder.query<User, string>({
      query: (id) => `/users/${id}`,
      providesTags: (result, error, id) =>
        result && id ? [{ type: "User" as const, id }] : ["User"],
    }),
    searchUser: builder.query({
      query: (name) => `search?name=${encodeURIComponent(name)}`,
      providesTags: (result, error, id) =>
        result
          ? [
              ...(result.users || []).map(({ id }) => ({ type: "User" as const, id })),
              ...(result.organizations || []).map(({ id }) => ({
                type: "Organization" as const,
                id,
              })),
            ]
          : [],
    }),
    updateUser: builder.mutation({
      query: (userData) => ({
        url: "/user",
        method: "PUT",
        body: userData,
      }),
      invalidatesTags: (result, error, arg) =>
        result?.id
          ? [{ type: "User" as const, id: result.id }]
          : ["User"],
    }),
    updateProfilePicture: builder.mutation<{}, { id: string; data: FormData }>({
      query: ({ data }) => {
        return {
          url: "/user/photo",
          method: "POST",
          body: data,
          formData: true,
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: "User" as const, id: arg.id },
        "Post",
      ],
    }),
    updateOrganizationProfilePicture: builder.mutation<
      {},
      { bodyFormData: FormData; orgId: string }
    >({
      query: ({ bodyFormData, orgId }) => {
        return {
          url: `/organizations/${orgId}/photo`,
          method: "POST",
          body: bodyFormData,
          formData: true,
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: "Organization", id: arg.orgId },
      ],
    }),
    addOrganization: builder.mutation<Organization, CreateOrganizationPayload>({
      query: (orgData) => ({
        url: `organizations`,
        method: "POST",
        body: sanitizeOrganizationPayload(orgData as Record<string, unknown>),
      }),
      invalidatesTags: (result, error, arg) =>  result ? [
        "Organization",
        "User",
      ]: ["Organization"],
    }),
    searchForMember: builder.query<{}, { orgId: string; email: string }>({
      query: (params) => ({
        url: `/organizations/${params.orgId}/invitations/search?email=${params.email}`,
        method: "GET",
      }),
    }),
    inviteMemberIntoOrganization: builder.mutation<
      {},
      { emails: { email: string; id: string | undefined }[]; orgId: string }
    >({
      query: ({ emails, orgId }) => ({
        url: `/organizations/${orgId}/invitations`,
        method: "POST",
        body: [...emails],
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "Organization", id: arg.orgId },
      ],
    }),
    acceptInvite: builder.mutation<{}, { orgId: string; inviterId: string; accept?: boolean }>({
      query: ({ orgId, inviterId, accept = true }) => ({
        url: `/organizations/${orgId}/invitations/accept-invite`,
        method: "POST",
        body: {
          inviterId,
          accept,
        }
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "Organization", id: arg.orgId },
        "User"
      ],
    }),
    getOrganizations: builder.query<Organization[], void>({
      query: () => ({
        url: '/organizations',
        method: 'GET'
      }),
      providesTags: (result,error, arg) => result ? [...result.map(({ id }) => ({ type: "Organization" as const, id }))] : ['Organization']
    }),
    getOrganizationProfile: builder.query<Organization, string>({
      query: (id: string) => ({
        url: `organizations/${id}/profile`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "Organization", id: id }],
    }),
    getOrganizationDetails: builder.query<Organization, string>({
      query: (id: string) => ({
        url: `organizations/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "Organization", id: id }],
    }),
    updateOrganization: builder.mutation<
      Organization,
      { orgId: string; orgData: Organization }
    >({
      query: ({ orgId, orgData }) => ({
        url: `organizations/${orgId}`,
        method: "PUT",
        body: sanitizeOrganizationPayload(orgData as Record<string, unknown>),
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "Organization", id: arg.orgId }
      ],
    }),
    deleteOrganization: builder.mutation<Organization, string>({
      query: (id: string) => ({
        url: `organizations/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, arg, id) => [{ type: "Organization", id }],
    }),
    updateMemberRole: builder.mutation<
      void,
      { orgId: string; memberId: string; role: "ADMIN" | "MEMBER" }
    >({
      query: (params) => ({
        url: `/organizations/${params.orgId}/members/${params.memberId}`,
        method: "PUT",
        body: { role: params.role },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "Organization" as const, id: arg.orgId },
        "User"
      ],
    }),
    removeMember: builder.mutation<void, RemoveMemberParams>({
      query: (params: RemoveMemberParams) => ({
        url: `/organizations/${params.orgId}/members/${params.memberId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "Organization", id: arg.orgId },
      ],
    }),
    getSuggested: builder.query<SuggestedUsersAndOrganizations, void>({
      query: () => ({
        url: `search/suggested`,
        method: "GET",
      }),
      transformResponse: (
        response: unknown
      ): SuggestedUsersAndOrganizations => {
        if (response == null || typeof response !== "object") {
          return { users: [], organizations: [] };
        }
        const raw = response as Record<string, unknown>;
        // Support direct { users, organizations } or wrapped { data: { users, organizations } }
        const data = (raw.data as Record<string, unknown>) ?? raw;
        const users = Array.isArray(data.users)
          ? data.users
          : Array.isArray(data.suggestedUsers)
            ? data.suggestedUsers
            : Array.isArray(raw.users)
              ? raw.users
              : Array.isArray(raw.suggestedUsers)
                ? raw.suggestedUsers
                : [];
        const organizations = Array.isArray(data.organizations)
          ? data.organizations
          : Array.isArray(data.suggestedOrganizations)
            ? data.suggestedOrganizations
            : Array.isArray(raw.organizations)
              ? raw.organizations
              : Array.isArray(raw.suggestedOrganizations)
                ? raw.suggestedOrganizations
                : [];
        return { users, organizations };
      },
      providesTags: (result, error, arg) => {
        if (!result) return [];
        const users = Array.isArray(result.users) ? result.users : [];
        const orgs = Array.isArray(result.organizations)
          ? result.organizations
          : [];
        return [
          ...users.map(({ id }) => ({ type: "User" as const, id })),
          "User",
          ...orgs.map(({ id }) => ({ type: "Organization" as const, id })),
        ];
      },
    }),
    getSparks: builder.query<Spark[], void>({
      query: () => ({
        url: "/lookups/sparks",
        method: "GET",
      }),
      providesTags: (result, error, arg) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Spark" as const, id })),
              "Spark",
            ]
          : ["Spark"],
    }),
    createOrganizationGoal: builder.mutation<
      {
        name: string;
        description: string;
        spark: Spark;
        organization: string;
        id: string;
      },
      {
        organizationId: string;
        goal: {
          name: string;
          description: string;
          spark: { name: string; id: string };
        };
      }
    >({
      query: (params) => ({
        url: `/organizations/${params.organizationId}/goals`,
        body: params.goal,
        method: "POST",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "Organization", id: arg.organizationId },
      ],
    }),
    editOrganizationGoal: builder.mutation<
      EditGoalResponsePayload,
      {
        organizationId: string;
        goalId: string;
        data: {
          name: string;
          description: string;
        };
      }
    >({
      query: (params) => ({
        url: `/organizations/${params.organizationId}/goals/${params.goalId}`,
        method: "PUT",
        body: params.data,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "Organization", id: arg.organizationId },
      ],
    }),
    deleteOrganizationGoal: builder.mutation<
      {},
      {
        orgId: string;
        goalId: string;
      }
    >({
      query: (params) => ({
        url: `/organizations/${params.orgId}/goals/${params.goalId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "Organization" as const, id: arg.orgId },
      ],
    }),
    addMetric: builder.mutation<
      {},
      {
        orgId: string;
        goalId: string;
        data: {
          description: string;
          startDate: string;
          endDate?: string;
          term: "QUARTERLY" | "MONTHLY";
          progress: {
            target: number;
            current: number;
            targetType: "DOLLARS" | "NUMBERS";
          };
        };
      }
    >({
      query: (params) => ({
        url: `/organizations/${params.orgId}/goals/${params.goalId}/metrics`,
        method: "POST",
        body: params.data,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "Organization" as const, id: arg.orgId },
        "Organization"
      ],
    }),
    addBlackFundMetric: builder.mutation<
      {},
      {
        orgId: string;
        goalId: string;
        data: {
          new_funding_secured: number; 
          increase_in_fundraising_confidence: string;
          full_staff: number;
          part_time_staff: number;
          active_volunteers: number;
          organizational_sustainability: string;
        };
      }
    >({
      query: (params) => ({
        url: `/organizations/${params.orgId}/goals/${params.goalId}/black-fund-metrics`,
        method: "POST",
        body: params.data,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "Organization" as const, id: arg.orgId },
        "Organization"
      ],
    }),
    updateMetric: builder.mutation<
      {},
      { orgId: string; goalId: string; metricId: string; data: Metric }
    >({
      query: (params) => ({
        url: `/organizations/${params.orgId}/goals/${params.goalId}/metrics/${params.metricId}`,
        method: "PUT",
        body: params.data,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "Organization" as const, id: arg.orgId },
      ],
    }),
    updateBlackFundMetric: builder.mutation<
      {},
      { orgId: string; goalId: string; metricId: string; data: BlackFundMetricsFormData }
    >({
      query: (params) => ({
        url: `/organizations/${params.orgId}/goals/${params.goalId}/black-fund-metrics/${params.metricId}`,
        method: "PUT",
        body: params.data,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "Organization" as const, id: arg.orgId },
      ],
    }),
    deleteMetric: builder.mutation<
      {},
      { orgId: string; goalId: string; metricId: string }
    >({
      query: (params) => ({
        url: `/organizations/${params.orgId}/goals/${params.goalId}/metrics/${params.metricId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "Organization" as const, id: arg.orgId },
      ],
    }),
    deleteBlackFundMetric: builder.mutation<
      {},
      { orgId: string; goalId: string; metricId: string }
    >({
      query: (params) => ({
        url: `/organizations/${params.orgId}/goals/${params.goalId}/black-fund-metrics/${params.metricId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "Organization" as const, id: arg.orgId },
      ],
    }),
    getMetricBySpark: builder.query<string[], string>({
      query: (id) => ({
        url: `/lookups/metrics/${id}`,
        method: "GET",
        providesTag: ["MetricDescription"],
      }),
      transformResponse: (response: any) => {
        // Transform the response to return just the metric names array
        return response.map((item: any) => item.metric);
      },
    }),
    getFunders: builder.query<any[], void>({
      query: () => ({
        url: `/lookups/funders`,
        method: "GET",
      }),
      providesTags: ["Funders"],
    }),
    getPrograms: builder.query<any[], string | void>({
      query: (organizationId) => ({
        url: `/lookups/programs${organizationId ? `?organizationId=${organizationId}` : ''}`,
        method: "GET",
      }),
      providesTags: ["Programs"],
    }),
    getFunderMetrics: builder.query<any[], string>({
      query: (funderId) => ({
        url: `/lookups/funder-metrics/${funderId}`,
        method: "GET",
      }),
      providesTags: ["FunderMetrics"],
    }),
    createPost: builder.mutation<Post, FormData>({
      query: (postData) => ({
        url: "/posts",
        method: "POST",
        body: postData,
        formData: true,
      }),
      invalidatesTags: [{ type: "Post" as const, id: "LIST" }],
    }),
    updatePost: builder.mutation<Post, { id: string; postData: FormData }>({
      query: ({ id, postData }) => ({
        url: `/posts/${id}`,
        method: "PUT",
        body: postData,
        formData: true,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "Post" as const, id: arg.id },
        { type: "Post" as const, id: "LIST" },
      ],
    }),
    getPostGroup: builder.query<Post[], string | void>({
      query: (userOrOrgId) => ({
        url: `/posts/group/${userOrOrgId}`,
        method: "GET",
      }),
      providesTags: (result, error, arg) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Post" as const, id })),
              { type: "Post" as const, id: "LIST" },
            ]
          : [{ type: "Post" as const, id: "LIST" }],
    }),
    getAllPosts: builder.query<Post[], { limit?: number; page?: number }>({
      query: ({ limit = 10, page = 1 }) => ({
        url: `/posts?limit=${limit}&page=${page}`,
        method: "GET",
      }),
      providesTags: (result, error, arg) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Post" as const, id })),
              { type: "Post" as const, id: "LIST" },
              "Post",
            ]
          : ["Post", { type: "Post" as const, id: "LIST" }],
    }),
    getPostById: builder.query<Post, string>({
      query: (id) => ({
        url: `/posts/${id}`,
        method: "GET",
        providesTags: (result, error, arg) => [
          { type: "Post" as const, id: result.id },
        ],
      }),
    }),
    deletePost: builder.mutation<void, string>({
      query: (id: string) => ({
        url: `/posts/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, argId) => [
        { type: "Post" as const, id: argId },
      ],
    }),
    addInteractionsToPost: builder.mutation<
      {
        type: string;
        data: { message: string };
        ownerId: string;
        postId: string;
        ownerType: string;
        id: string;
      },
      { id: string; type: InteractionType; data?: { message: string } }
    >({
      query: (postInteraction) => {
        return {
          url: `/posts/${postInteraction.id}/interactions`,
          method: "POST",
          body: {
            type: postInteraction.type,
            data: postInteraction?.data || undefined,
          },
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: "Post" as const, id: arg.id },
      ],
    }),
    updateCommentOnPost: builder.mutation<
      {
        id: string;
        postId: string;
        type: string;
        data: { message: string };
      },
      { postId: string; interactionId: string; data: { message: string } }
    >({
      query: ({ postId, interactionId, data }) => ({
        url: `/posts/${postId}/interactions/${interactionId}`,
        method: "PUT",
        body: { data },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "Post" as const, id: arg.postId },
      ],
    }),
    deleteCommentFromPost: builder.mutation<
      void,
      { postId: string; interactionId: string }
    >({
      query: ({ postId, interactionId }) => ({
        url: `/posts/${postId}/interactions/${interactionId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "Post" as const, id: arg.postId },
      ],
    }),
    getStates: builder.query<State[], void>({
      query: () => ({
        url: '/lookups/us-states',
        method: 'GET'
      }),
      providesTags: ["State"]
    }),
    getState: builder.query<State, { state: string }>({
      query: ({ state }) => ({
        url: `/lookups/us-states/${state}`,
        method: 'GET'
      }),
      providesTags: (result, error, arg) => ["State", { type: "State", id: arg.state }]
    }),
    reverseGeocoding: builder.mutation<{ longitude: number, latitude: number; display_name: string}, { longitude: number; latitude: number }>({
      query: ({ longitude, latitude }) => ({
        url: '/organizations/forward-geocoding',
        method: 'POST',
        body: {
          longitude, 
          latitude
        }
      }),
    }),
    fowardGeocoding: builder.mutation<{ longitude: number, latitude: number; display_name: string}, { addressJson: Location }>({
      query: ({ addressJson }) => ({
        url: '/organizations/forward-geocoding',
        method: 'POST',
        body: {
          addressJson
        }
      }),
    }),
    getCities: builder.query<Cities, { limit?: number; offset?: number; namePrefix?: string; state: string}>({
      query: ({ state, limit, offset, namePrefix}) => ({
        url: `/lookups/us-cities/${state}?limit=${limit || 10}&offset=${offset || 0}${namePrefix ? `&namePrefix=${namePrefix}` : ''}`,
        method: "GET",
      }),
      providesTags: (result, error, arg) =>
        result
          ? [
              ...result.cities.map(({ id }) => ({ type: "City" as const, id })),
              { type: "City" as const, id: "LIST" },
              "City",
            ]
          : ["City", { type: "City" as const, id: "LIST" }],
    }),
    // Reports
    getReports: builder.query<Report[], void>({
      query: () => ({
        url: "/reports",
        method: "GET",
      }),
      providesTags: ["Report"],
    }),
    getReportById: builder.query<Report, { id: string }>({
      query: ({ id }) => ({
        url: `/reports/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, arg) =>
        result ? [{ type: "Report" as const, id: arg.id }, "Report"] : ["Report"],
    }),
    createReport: builder.mutation<Report, CreateReportPayload>({
      query: (payload) => ({
        url: "/reports",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Report"],
    }),
    updateReport: builder.mutation<Report, { id: string; data: CreateReportPayload }>({
      query: ({ id, data }) => ({
        url: `/reports/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, arg) =>
        result
          ? [{ type: "Report" as const, id: arg.id }, "Report"]
          : ["Report"],
    }),
    deleteReport: builder.mutation<{ message: string }, { id: string }>({
      query: ({ id }) => ({
        url: `/reports/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Report"],
    }),
    getReportPDF: builder.query<{ report: Report }, { id: string }>({
      query: ({ id }) => ({
        url: `/reports/${id}/pdf`,
        method: "GET",
      }),
    }),
    generateAIReport: builder.mutation<
      Report,
      { funderId?: string; programId?: string; startDate: string; endDate: string; audience?: string; organizationId?: string }
    >({
      query: (payload) => ({
        url: "/reports/generate-ai",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Report"],
    }),
    // ── Billing ──────────────────────────────────────────────────────────────
    createCheckoutSession: builder.mutation<{ url: string }, { successUrl?: string; cancelUrl?: string } | void>({
      query: (payload) => ({
        url: "/billing/create-checkout-session",
        method: "POST",
        body: payload ?? {},
      }),
    }),
    confirmCheckoutSession: builder.mutation<{ ok: boolean }, { session_id: string }>({
      query: ({ session_id }) => ({
        url: `/billing/confirm-session?session_id=${session_id}`,
        method: "GET",
      }),
      invalidatesTags: ["User"],
    }),
    // ── User Documents ────────────────────────────────────────────────────────
    // Backend returns a plain array of documents; no wrapper object.
    getMyDocuments: builder.query<any[], void>({
      query: () => "/user/documents",
      providesTags: ["User"],
    }),
    uploadUserDocument: builder.mutation<any, FormData>({
      query: (formData) => ({
        url: "/user/documents",
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["User"],
    }),
    deleteUserDocument: builder.mutation<any, { documentId: string }>({
      query: ({ documentId }) => ({
        url: `/user/documents/${documentId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["User"],
    }),
    // Download document (returns blob; use with useLazyDownloadUserDocumentQuery)
    downloadUserDocument: builder.query<Blob, string>({
      query: (id) => ({
        url: `/user/documents/${id}/download`,
        responseHandler: (response) => response.blob(),
      }),
    }),
  }),
});

export const {
  // notifications and tag
  useGetNotificationsQuery,
  useDeleteNotificationsMutation,
  useViewNotificationsMutation,
  useTagUserMutation,
  useGetUsersQuery,
  // profile
  useGetProfileQuery,
  useLazyGetProfileQuery,
  useUpdateProfilePictureMutation,
  useDeactivateUserMutation,
  // user
  useGetUserByIdQuery,
  useLazyGetUserByIdQuery,
  useUpdateUserMutation,
  useLazySearchUserQuery,
  // following
  useFollowUserMutation,
  useGetFollowersQuery,
  useLazyGetFollowersQuery,
  useGetUsersFollowedQuery,
  useLazyGetUsersFollowedQuery,
  useUnFollowAccountMutation,
  // post
  useLazyGetPostGroupQuery,
  useGetPostGroupQuery,
  useLazyGetAllPostsQuery,
  useGetAllPostsQuery,
  useCreatePostMutation,
  useUpdatePostMutation,
  useDeletePostMutation,
  useLazyGetPostByIdQuery,
  // interactions
  useAddInteractionsToPostMutation,
  useUpdateCommentOnPostMutation,
  useDeleteCommentFromPostMutation,
  // organization
  useGetOrganizationDetailsQuery,
  useLazyGetOrganizationDetailsQuery,
  useGetOrganizationProfileQuery, 
  useLazyGetOrganizationProfileQuery,
  useDeleteOrganizationMutation,
  useAddOrganizationMutation,
  useUpdateOrganizationMutation,
  useUpdateOrganizationProfilePictureMutation,
  useGetOrganizationsQuery,
  useLazyGetOrganizationsQuery,
  // goals
  useCreateOrganizationGoalMutation,
  useEditOrganizationGoalMutation,
  useDeleteOrganizationGoalMutation,
  // metric
  useUpdateMetricMutation,
  useUpdateBlackFundMetricMutation,
  useAddMetricMutation,
  useAddBlackFundMetricMutation,
  useGetMetricBySparkQuery,
  useLazyGetMetricBySparkQuery,
  useDeleteMetricMutation,
  useDeleteBlackFundMetricMutation,
  useGetFundersQuery,
  useGetProgramsQuery,
  useGetFunderMetricsQuery,
  // Member
  useRemoveMemberMutation,
  useInviteMemberIntoOrganizationMutation,
  useLazySearchForMemberQuery,
  useUpdateMemberRoleMutation,
  useAcceptInviteMutation,
  // other
  useGetSuggestedQuery,
  // spark
  useGetSparksQuery,
  // world cities
  useLazyGetCitiesQuery,
  useLazyGetStatesQuery,
  useGetStatesQuery,
  useLazyGetStateQuery,
  useGetStateQuery,
  useGetCitiesQuery,
  useFowardGeocodingMutation,
  useReverseGeocodingMutation,
  // reports
  useGetReportsQuery,
  useGetReportByIdQuery,
  useLazyGetReportByIdQuery,
  useCreateReportMutation,
  useUpdateReportMutation,
  useDeleteReportMutation,
  useGenerateAIReportMutation,
  useLazyGetReportPDFQuery,
  // billing
  useCreateCheckoutSessionMutation,
  useConfirmCheckoutSessionMutation,
  // user documents
  useGetMyDocumentsQuery,
  useLazyDownloadUserDocumentQuery,
  useUploadUserDocumentMutation,
  useDeleteUserDocumentMutation,
} = api;
